import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Send, MessageCircle, Mail, Phone, MapPin, Facebook, Instagram, Music, Link2, Check, X, Clock, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { trackFormSubmission, trackButtonClick } from '../lib/analytics';

const Contact = () => {
  const location = useLocation();
  const navigate = useNavigate();
  type ConfirmState = 'success' | 'expired' | 'invalid' | 'error' | null;
  const [confirmState, setConfirmState] = useState<ConfirmState>(null);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('confirmed');
    if (!raw) return;

    let next: ConfirmState = null;
    if (raw === '1') next = 'success';
    else if (raw === 'expired') next = 'expired';
    else if (raw === 'invalid') next = 'invalid';
    else if (raw === 'error') next = 'error';

    if (next) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      setConfirmState(next);
      if (next === 'success') {
        setShowBanner(true);
        window.setTimeout(() => setShowBanner(false), 8000);
      }
    }

    params.delete('confirmed');
    const nextSearch = params.toString();
    navigate(
      { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!confirmState) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setConfirmState(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirmState]);

  const confirmCopy = (() => {
    switch (confirmState) {
      case 'success':
        return {
          icon: <Check className="h-7 w-7 text-emerald-400" />,
          iconWrap: 'bg-emerald-500/10 border-emerald-500/20',
          title: 'Email confirmed',
          body: 'Thanks for verifying your email. Your message is on its way to our team and we\u2019ll be in touch within 24 hours.',
        };
      case 'expired':
        return {
          icon: <Clock className="h-7 w-7 text-amber-400" />,
          iconWrap: 'bg-amber-500/10 border-amber-500/20',
          title: 'Link expired',
          body: 'That confirmation link is more than 24 hours old. Please send your message again to get a fresh link.',
        };
      case 'invalid':
        return {
          icon: <AlertCircle className="h-7 w-7 text-rose-400" />,
          iconWrap: 'bg-rose-500/10 border-rose-500/20',
          title: 'Link not valid',
          body: 'This confirmation link is invalid or has already been used. Please send your message again if you need to.',
        };
      case 'error':
        return {
          icon: <AlertCircle className="h-7 w-7 text-rose-400" />,
          iconWrap: 'bg-rose-500/10 border-rose-500/20',
          title: 'Something went wrong',
          body: 'We couldn\u2019t confirm right now. Please try the link again in a few minutes or send us a new message.',
        };
      default:
        return null;
    }
  })();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    business: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checkInbox, setCheckInbox] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    if (!supabaseUrl || !supabaseKey) {
      toast.error('Configuration error. Please contact us directly.');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch(`${supabaseUrl}/functions/v1/send-contact-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          business: formData.business,
          message: formData.message,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send message');
      }

      toast.success('Check your inbox to confirm your message.');
      setCheckInbox(true);

      trackFormSubmission('Contact Form', {
        business_type: formData.business
      });

      setFormData({
        name: '',
        email: '',
        business: '',
        message: '',
      });
    } catch (error: any) {
      console.error('Contact Form Error:', error);
      toast.error(error?.message || 'Failed to send message. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleWhatsApp = () => {
    trackButtonClick('WhatsApp', 'Contact');
    window.open('https://wa.me/4407624474095', '_blank');
  };

  const socialLinks = [
    {
      name: 'Facebook',
      href: 'https://www.facebook.com/profile.php?id=61573912922638',
      icon: Facebook,
    },
    {
      name: 'Instagram',
      href: 'https://www.instagram.com/aiwebsolutionsiom/',
      icon: Instagram,
    },
    {
      name: 'TikTok',
      href: 'https://www.tiktok.com/@ai.websolutions',
      icon: Music,
    },
    {
      name: 'Links',
      href: 'https://aiwebsolutions.social/',
      icon: Link2,
    },
  ];

  return (
    <>
    {confirmState && confirmCopy && (
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/70 backdrop-blur-md animate-in fade-in duration-200"
        onClick={() => setConfirmState(null)}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirmed-title"
      >
        <div
          className="relative w-full max-w-md rounded-3xl bg-gradient-to-br from-white/[0.06] to-white/[0.02] border border-white/[0.08] p-8 sm:p-10 text-center shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="absolute inset-x-0 top-0 h-[3px] rounded-t-3xl bg-gradient-to-r from-fuchsia-500 via-cyan-400 to-fuchsia-500" />
          <button
            type="button"
            onClick={() => setConfirmState(null)}
            className="absolute top-4 right-4 p-2 rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.05] transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
          <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl border mb-6 ${confirmCopy.iconWrap}`}>
            {confirmCopy.icon}
          </div>
          <h2 id="confirmed-title" className="text-2xl sm:text-3xl font-semibold text-white mb-3">
            {confirmCopy.title}
          </h2>
          <p className="text-gray-400 leading-relaxed text-sm sm:text-base mb-8">
            {confirmCopy.body}
          </p>
          <button
            type="button"
            onClick={() => setConfirmState(null)}
            className="btn-primary w-full"
          >
            Got it
          </button>
        </div>
      </div>
    )}
    <section className="min-h-[100svh] pt-24 lg:pt-28 relative">
      {showBanner && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-40 max-w-md w-[calc(100%-2rem)] px-4 py-3 rounded-xl bg-emerald-500/[0.08] border border-emerald-500/25 backdrop-blur-md shadow-lg flex items-start gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
          <div className="mt-0.5 inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500/20 flex-shrink-0">
            <Check className="h-3.5 w-3.5 text-emerald-300" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-emerald-100">Email confirmed</p>
            <p className="text-xs text-emerald-200/70 mt-0.5">Your message is on its way to our team. We&rsquo;ll be in touch within 24 hours.</p>
          </div>
          <button
            type="button"
            onClick={() => setShowBanner(false)}
            className="text-emerald-200/60 hover:text-emerald-100 transition-colors flex-shrink-0"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="text-center mb-16 lg:mb-20">
          <h1 className="page-title text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mb-5">
            Let's Talk
          </h1>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          <div className="space-y-10">
            <div>
              <h2 className="text-lg font-medium text-white mb-4">How can we help?</h2>
              <p className="text-gray-500 leading-relaxed text-[15px]">
                Whether you have a clear project in mind or just a rough idea, we're happy to have a conversation about what would work best for your business. No obligation, no jargon — just honest advice.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center mr-4 flex-shrink-0">
                  <MapPin className="h-4 w-4 text-primary/70" />
                </div>
                <div>
                  <h3 className="text-white font-medium text-[15px]">Isle of Man Based</h3>
                  <p className="text-gray-500 text-sm">Serving clients across the UK</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center mr-4 flex-shrink-0">
                  <Mail className="h-4 w-4 text-primary/70" />
                </div>
                <div>
                  <h3 className="text-white font-medium text-[15px]">Email Us</h3>
                  <a
                    href="mailto:aiwebsolutionsiom@gmail.com"
                    className="text-gray-500 text-sm hover:text-gray-300 transition-colors"
                  >
                    aiwebsolutionsiom@gmail.com
                  </a>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center mr-4 flex-shrink-0">
                  <Phone className="h-4 w-4 text-primary/70" />
                </div>
                <div>
                  <h3 className="text-white font-medium text-[15px]">Call or WhatsApp</h3>
                  <a
                    href="tel:+4407624474095"
                    className="text-gray-500 text-sm hover:text-gray-300 transition-colors"
                  >
                    +44 7624 474095
                  </a>
                </div>
              </div>
            </div>

            <button
              onClick={handleWhatsApp}
              className="btn-secondary flex items-center"
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              Message on WhatsApp
            </button>

            <div className="pt-8 border-t border-white/[0.06]">
              <h3 className="text-white font-medium text-sm mb-4">Follow Us</h3>
              <div className="flex gap-2.5">
                {socialLinks.map((social) => (
                  <a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackButtonClick(social.name, 'Contact Social')}
                    className="w-9 h-9 rounded-lg bg-white/[0.03] border border-white/[0.06] flex items-center justify-center text-gray-500 hover:text-gray-300 hover:border-white/10 transition-all duration-300"
                    aria-label={`Follow us on ${social.name}`}
                  >
                    <social.icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div>
            <div className="rounded-2xl p-8 bg-white/[0.02] border border-white/[0.05] relative">
              <h2 className="text-lg font-medium text-white mb-8">Send us a message</h2>

              {checkInbox ? (
                <div className="text-center py-8">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 mb-6">
                    <Check className="h-6 w-6 text-emerald-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">Check your inbox</h3>
                  <p className="text-gray-400 max-w-sm mx-auto leading-relaxed text-sm">
                    We&rsquo;ve sent you a confirmation link. Click it and your message will come straight to us.
                  </p>
                  <button
                    type="button"
                    onClick={() => setCheckInbox(false)}
                    className="mt-8 text-sm text-gray-500 hover:text-gray-300 transition-colors"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-400 mb-2.5">
                    Your Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3.5 bg-white/[0.03] border border-white/[0.06] rounded-xl text-white placeholder-gray-600 focus:border-white/20 focus:ring-0 focus:outline-none transition-colors"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-400 mb-2.5">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3.5 bg-white/[0.03] border border-white/[0.06] rounded-xl text-white placeholder-gray-600 focus:border-white/20 focus:ring-0 focus:outline-none transition-colors"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="business" className="block text-sm font-medium text-gray-400 mb-2.5">
                    Business / Industry
                  </label>
                  <input
                    type="text"
                    name="business"
                    id="business"
                    value={formData.business}
                    onChange={handleChange}
                    className="w-full px-4 py-3.5 bg-white/[0.03] border border-white/[0.06] rounded-xl text-white placeholder-gray-600 focus:border-white/20 focus:ring-0 focus:outline-none transition-colors"
                    placeholder="e.g. Retail, Professional Services"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-400 mb-2.5">
                    How can we help?
                  </label>
                  <textarea
                    name="message"
                    id="message"
                    rows={5}
                    required
                    minLength={10}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3.5 bg-white/[0.03] border border-white/[0.06] rounded-xl text-white placeholder-gray-600 focus:border-white/20 focus:ring-0 focus:outline-none transition-colors resize-none"
                    placeholder="Tell us about your project or ask us anything..."
                  />
                  <div className="mt-2 flex items-center justify-between text-xs">
                    <span className={`transition-colors ${
                      formData.message.length === 0
                        ? 'text-gray-600'
                        : formData.message.length < 10
                        ? 'text-amber-400/80'
                        : 'text-emerald-400/80'
                    }`}>
                      {formData.message.length === 0
                        ? 'Please write at least 10 characters'
                        : formData.message.length < 10
                        ? `${10 - formData.message.length} more character${10 - formData.message.length === 1 ? '' : 's'} needed`
                        : 'Looks good'}
                    </span>
                    <span className="text-gray-600">{formData.message.length} / 10</span>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || formData.message.trim().length < 10}
                  className={`w-full btn-primary flex items-center justify-center ${
                    isSubmitting || formData.message.trim().length < 10 ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      Send Message
                      <Send className="ml-2 h-4 w-4" />
                    </>
                  )}
                </button>

                <p className="text-center text-gray-600 text-xs">
                  We typically respond within 24 hours
                </p>
              </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
    </>
  );
};

export default Contact;
